import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import React, { type ChangeEvent, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useBoolean } from 'react-use'

import { SearchField } from 'components/common/SearchField'
import {
  filterNamespaceRequests,
  filterNamespaces,
  NAMESPACE_CHIP_LIST_MAX_HEIGHT
} from 'components/configurations/clusters/clusters-utils'
import { PendingNamespaceAccessRequestChip } from 'components/configurations/common/PendingNamespaceAccessRequestChip'
import { SharedNamespaceChip } from 'components/configurations/common/SharedNamespaceChip'
import { UnsharedNamespaceChip } from 'components/configurations/common/UnsharedNamespaceChip'
import Icon from 'components/Icon'
import { createRequests } from 'components/kubernetes/access-control/utils'
import useAuth from 'hooks/use-auth'
import {
  type OutgoingRequestByAssetId,
  useManageSharedAssets
} from 'hooks/use-manage-shared-assets'
import toast from 'lib/toast'
import { muiTheme } from 'mui-theme'
import { sharedAssetsQueryKeys } from 'queries/sharedAssets/query-keys'
import { usePostSharedAssetsRequests } from 'queries/sharedAssets/use-post-shared-assets-requests'
import { usePostSharedAssetsRequestsWithAction } from 'queries/sharedAssets/use-post-shared-assets-requests-with-action'
import {
  SharedAssetRequestStatus,
  SharedAssetsRequestAction
} from 'types/api/sharedAssets/requests'
import { messageFromResponseError } from 'utils/strings'

import type { AvailableK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

export const AvailableClusterNamespaceAccessDrawerContent: React.FC<{
  activeCluster: AvailableK8sClusterAsset
  handleCloseDrawer(): void
}> = ({ activeCluster, handleCloseDrawer }) => {
  const { currentTeamId: teamId } = useAuth()
  const queryClient = useQueryClient()

  const [searchValue, setSearchValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const [selectedNamespacesToAdd, setSelectedNamespacesToAdd] = useState<
    string[]
  >([])
  const [
    pendingNamespaceRequestsToRemove,
    setPendingNamespaceRequestsToRemove
  ] = useState<OutgoingRequestByAssetId[]>([])
  const [
    approvedNamespaceRequestsToRemove,
    setApprovedNamespaceRequestsToRemove
  ] = useState<OutgoingRequestByAssetId[]>([])
  const [isSelectAllUnsharedNamespaces, setIsSelectAllUnsharedNamespaces] =
    useBoolean(false)

  const {
    getAllOutgoingRequestsForCluster,
    getPendingOutgoingRequestsForCluster
  } = useManageSharedAssets()

  const { assetId, availableNamespaces, clusterName } = activeCluster

  const allPendingOutgoingRequests =
    getPendingOutgoingRequestsForCluster(assetId)
  const allApprovedOutgoingRequests = getAllOutgoingRequestsForCluster(
    assetId
  ).filter(
    (request) => request.currentStatus === SharedAssetRequestStatus.APPROVED
  )

  const { mutateAsync: mutateDeleteSharedNamespaceRequest } =
    usePostSharedAssetsRequestsWithAction(SharedAssetsRequestAction.DELETE)
  const { mutateAsync: mutatePostSharedAssetsRequests } =
    usePostSharedAssetsRequests()

  const handleSubmit = async () => {
    setIsSubmitting(true)

    // Get the list of currently approved requests to deny
    const approvedRequestIdsToDelete = approvedNamespaceRequestsToRemove.map(
      (request) => request.requestId
    )

    const pendingRequestIdsToDelete = pendingNamespaceRequestsToRemove.map(
      (request) => request.requestId
    )

    const requestIdsToDelete = [
      ...approvedRequestIdsToDelete,
      ...pendingRequestIdsToDelete
    ]

    // Filter out the namespaces that are already approved for the active team. We don't need to create requests for these.
    const newNamespacesToAdd = selectedNamespacesToAdd.filter((namespace) => {
      return !allApprovedOutgoingRequests.some(
        (request) => request.namespace === namespace
      )
    })

    const requestsToCreate = createRequests({
      owningTeamId: activeCluster.owningTeamId,
      requestingTeamId: teamId,
      assetId,
      namespaces: newNamespacesToAdd
    })

    try {
      if (requestIdsToDelete.length > 0) {
        await mutateDeleteSharedNamespaceRequest({
          requestIds: requestIdsToDelete
        })
      }

      if (requestsToCreate.length > 0) {
        await mutatePostSharedAssetsRequests({
          newRequests: requestsToCreate
        })
      }

      setIsSubmitting(false)
      handleCloseDrawer()

      toast({
        type: 'success',
        text: 'Successfully submitted requests'
      })

      await queryClient.refetchQueries(sharedAssetsQueryKeys.get({ teamId }))
    } catch (e) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          e,
          'An error occurred while processing this request'
        )
      })
      setIsSubmitting(false)
    }
  }

  // Filter out the pending namespace requests that are marked for removal
  const pendingNamespaceRequests = useMemo(() => {
    const pendingRequestIdsToRemove = new Set(
      pendingNamespaceRequestsToRemove.map((request) => request.requestId)
    )

    return allPendingOutgoingRequests.filter(
      (request) => !pendingRequestIdsToRemove.has(request.requestId)
    )
  }, [allPendingOutgoingRequests, pendingNamespaceRequestsToRemove])

  // Filter out the approved namespace requests that are marked for removal
  const approvedNamespaceRequests = useMemo(() => {
    const approvedNamespacesToRemove = new Set(
      approvedNamespaceRequestsToRemove.map(({ namespace }) => namespace)
    )

    return allApprovedOutgoingRequests.filter(
      (request) => !approvedNamespacesToRemove.has(request.namespace)
    )
  }, [allApprovedOutgoingRequests, approvedNamespaceRequestsToRemove])

  const unsharedNamespaces = useMemo(() => {
    const pendingRequestsNamespaces = pendingNamespaceRequests.map(
      ({ namespace }) => namespace
    )
    const approvedRequestsNamespaces = approvedNamespaceRequests.map(
      ({ namespace }) => namespace
    )

    const allRequestNamespaces = new Set([
      ...pendingRequestsNamespaces,
      ...approvedRequestsNamespaces
    ])

    return availableNamespaces.filter(
      (namespace) => !allRequestNamespaces.has(namespace)
    )
  }, [approvedNamespaceRequests, availableNamespaces, pendingNamespaceRequests])

  /**
   * Search Filtering
   */
  const filteredPendingNamespaceRequests = useMemo(
    () => filterNamespaceRequests(pendingNamespaceRequests, searchValue),
    [pendingNamespaceRequests, searchValue]
  )

  const filteredApprovedNamespaceRequests = useMemo(
    () => filterNamespaceRequests(approvedNamespaceRequests, searchValue),
    [approvedNamespaceRequests, searchValue]
  )

  // Get the list of available namespaces that are not in the pending or approved requests marked for removal
  const filteredUnsharedNamespaces = useMemo(
    () => filterNamespaces(unsharedNamespaces, searchValue),
    [searchValue, unsharedNamespaces]
  )
  /**
   * End Search Filtering
   */

  // Adds a namespace request to the list of pending requests to remove
  const handleDeleteSharedNamespaceRequest = (
    request: OutgoingRequestByAssetId
  ) => {
    setPendingNamespaceRequestsToRemove((prevState) => [...prevState, request])
  }

  // Adds a namespace request to the list of approved requests (shared namespaces) to remove
  const handleDeleteSharedNamespaceAccess = (
    request: OutgoingRequestByAssetId
  ) => {
    setApprovedNamespaceRequestsToRemove((prevState) => [...prevState, request])
  }

  // Toggles the selection of an unshared namespace
  const handleSelectUnsharedNamespace = (
    namespace: string,
    isSelected: boolean
  ) => {
    const newSelectedNamespacesToAdd = isSelected
      ? selectedNamespacesToAdd.filter(
          (selectedNamespace) => selectedNamespace !== namespace
        )
      : [...selectedNamespacesToAdd, namespace]

    if (isSelectAllUnsharedNamespaces) {
      setIsSelectAllUnsharedNamespaces(false)
    }

    setSelectedNamespacesToAdd(newSelectedNamespacesToAdd)
  }

  // Toggles the "Select All" checkbox for unshared namespaces
  const handleSelectAllUnsharedNamespaces = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setIsSelectAllUnsharedNamespaces(event.target.checked)

    if (event.target.checked) {
      setSelectedNamespacesToAdd(filteredUnsharedNamespaces)
    } else {
      setSelectedNamespacesToAdd([])
    }
  }

  const isSaveDisabled =
    selectedNamespacesToAdd.length === 0 &&
    pendingNamespaceRequestsToRemove.length === 0 &&
    approvedNamespaceRequestsToRemove.length === 0

  return (
    // TODO: useFlexGap
    <Stack
      data-testid={`available-cluster-namespaces-drawer-content-${assetId}`}
      sx={{ rowGap: 4 }}
    >
      <Stack sx={{ rowGap: 1 }}>
        <Typography variant="h6">
          <Icon
            css={{ marginRight: muiTheme.spacing(1) }}
            icon="dharmachakra"
          />
          {clusterName}
        </Typography>
        <Typography variant="body1">
          Manage namespace access requests
        </Typography>
      </Stack>
      <SearchField
        fullWidth
        data-testid="available-cluster-namespace-access-drawer-search"
        helperText="Search by namespace"
        label="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Stack sx={{ rowGap: 4 }}>
        {filteredPendingNamespaceRequests.length > 0 && (
          <Box data-testid="pending-namespace-requests-list">
            {filteredPendingNamespaceRequests.map((request) => {
              const { namespace, requester } = request

              return (
                <PendingNamespaceAccessRequestChip
                  key={namespace}
                  isRequesting={true}
                  namespace={namespace}
                  requester={requester}
                  onDelete={() => handleDeleteSharedNamespaceRequest(request)}
                />
              )
            })}
          </Box>
        )}
        {filteredUnsharedNamespaces.length > 0 && (
          <Stack sx={{ rowGap: 1 }}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Available namespaces</Typography>
                <Typography variant="caption">
                  Select namespaces to request access
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', justifyContent: 'end' }} xs={6}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelectAllUnsharedNamespaces}
                        onChange={handleSelectAllUnsharedNamespaces}
                      />
                    }
                    label="Select All"
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box
              data-testid="unshared-namespace-list"
              sx={{
                maxHeight: NAMESPACE_CHIP_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                padding: 1
              }}
            >
              {filteredUnsharedNamespaces.map((namespace) => {
                const isSelected = selectedNamespacesToAdd.includes(namespace)

                return (
                  <UnsharedNamespaceChip
                    key={namespace}
                    isSelected={isSelected}
                    namespace={namespace}
                    onClick={() =>
                      handleSelectUnsharedNamespace(namespace, isSelected)
                    }
                  />
                )
              })}
            </Box>
          </Stack>
        )}
        {filteredApprovedNamespaceRequests.length > 0 && (
          <Stack sx={{ rowGap: 1 }}>
            <Typography>Namespaces currently shared with your team</Typography>
            <Box
              data-testid="shared-namespace-list"
              sx={{
                maxHeight: NAMESPACE_CHIP_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                padding: 1
              }}
            >
              {filteredApprovedNamespaceRequests.map((request) => {
                const { namespace } = request

                return (
                  <SharedNamespaceChip
                    key={namespace}
                    namespace={namespace}
                    onDelete={() => handleDeleteSharedNamespaceAccess(request)}
                  />
                )
              })}
            </Box>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" sx={{ columnGap: 2 }}>
        <LoadingButton
          color="primary"
          disabled={isSaveDisabled}
          loading={isSubmitting}
          variant="contained"
          onClick={handleSubmit}
        >
          Save
        </LoadingButton>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleCloseDrawer}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  )
}
